import { Decision } from '../../../common/constants/decision';
import {
  filterToGuidelineRuleSets,
  stripWorkflowSuffix,
} from '../../../common/helpers/rule-results/filter';
import {
  getMessageFromDecision,
  isSaharaDeclineToRuw,
} from '../decision/decisionMessages';
import { IDecision } from '../interfaces/IDecision';
import { IRuleResults } from '../interfaces/IRuleResults';
import { ReferralCategory, ReferralReason } from './types';

const CATEGORY_PRIORITY: Record<ReferralCategory, number> = {
  [ReferralCategory.CARRIER_ELIGIBILITY]: 0,
  [ReferralCategory.TRL_DOWNGRADE]: 1,
  [ReferralCategory.PRIOR_POLICY_RISK]: 2,
  [ReferralCategory.MEDICAL]: 3,
  [ReferralCategory.OTHER]: 4,
};

// Word-boundary match catches both unprefixed ("Medical Hypertension") and
// prefixed ("PIA Medical Claim", "LGA Medical Diabetes") rule sets, while
// avoiding false positives on words that contain "medical" as a substring
// (e.g., "telemedical", "premedical"). Maestro emits prefixed forms via
// snapshotRuleTransform.ts when the trace map's `ruleset` field already
// carries a product/context prefix.
const MEDICAL_RULE_SET = /\bmedical\b/i;

// Medical takes priority via the explicit word-boundary regex; other
// categories share the same substring patterns as message-string matching
// so a rule set named "TRL Downgrade" / "Carrier Eligibility Rules" /
// "Prior Application Issues" lands in the right category.
export function categorizeRuleSet(ruleSetName: string): ReferralCategory {
  if (MEDICAL_RULE_SET.test(ruleSetName)) return ReferralCategory.MEDICAL;
  return categorizeMessage(ruleSetName);
}

// Substring patterns anchored on the actual TRIGGER context of each Maestro
// constant (verified by tracing usage in maestro/src/), not the literal text.
// Contract pins are enforced in __tests__/helpers.test.ts — update both files
// together when adding new patterns.
export function categorizeMessage(message: string): ReferralCategory {
  const normalized = message.toLowerCase();
  // Carrier-eligibility-API-driven RUW (LGA/Protective). Maestro typoed the
  // legacy missing-data constant as "eligiblity" in global.constants.ts.
  if (
    normalized.includes('carrier eligibility') ||
    normalized.includes('eligiblity')
  ) {
    return ReferralCategory.CARRIER_ELIGIBILITY;
  }
  // Prior-policy-driven RUW. " clr" matches RUW_DUE_TO_CLR (a prior policy
  // entered Coverage Limitation Rider). Leading space avoids false positives
  // on words that happen to contain "clr".
  if (
    normalized.includes('prior application') ||
    normalized.includes('previous decline') ||
    normalized.includes('reinstatement') ||
    normalized.includes('incomplete pia') ||
    normalized.includes('rescission') ||
    normalized.includes(' clr')
  ) {
    return ReferralCategory.PRIOR_POLICY_RISK;
  }
  // Strictly TRL — bucketing / generic class-downgrade / eligibility-downgrade
  // strings deliberately fall through to OTHER so the "TRL DOWNGRADE" card
  // title doesn't misrepresent them.
  if (normalized.includes('trl downgrade')) {
    return ReferralCategory.TRL_DOWNGRADE;
  }
  return ReferralCategory.OTHER;
}

function titleForCategory(category: ReferralCategory): string {
  switch (category) {
    case ReferralCategory.CARRIER_ELIGIBILITY:
      return 'CARRIER ELIGIBILITY';
    case ReferralCategory.TRL_DOWNGRADE:
      return 'TRL DOWNGRADE';
    case ReferralCategory.PRIOR_POLICY_RISK:
      return 'PRIOR POLICY RISK';
    case ReferralCategory.MEDICAL:
      return 'MEDICAL';
    case ReferralCategory.OTHER:
      return 'REFERRAL REASON';
  }
}

function decisionMessageReasons(
  decisions: IDecision[],
  saharaDeclineToRuw: boolean,
): ReferralReason[] {
  const reasons: ReferralReason[] = [];
  decisions
    .filter(decision => !decision.ignored && !decision.deleteReason)
    .forEach(decision => {
      getMessageFromDecision(decision, saharaDeclineToRuw).forEach(message => {
        const trimmed = message.trim();
        if (!trimmed) return;
        const category = categorizeMessage(trimmed);
        reasons.push({
          id: `message_${trimmed}`,
          category,
          title: titleForCategory(category),
          body: trimmed,
        });
      });
    });
  return reasons;
}

function ruleSetReasons(
  ruleResults: IRuleResults | undefined,
): ReferralReason[] {
  if (!ruleResults?.ruleSets?.length) return [];
  return (
    filterToGuidelineRuleSets(ruleResults.ruleSets)
      // Surface any rule set that displays the orange-exclamation icon in
      // the existing rule-result UI — i.e. either its overall outcome is RUW
      // or at least one matched rule carries an RUW decision.
      .filter(
        ruleSet =>
          ruleSet.outcome?.decision === Decision.RUW ||
          ruleSet.matchedRules.some(rule => rule.decision === Decision.RUW),
      )
      .map(ruleSet => {
        const cleanName = stripWorkflowSuffix(ruleSet.ruleSetName);
        const category = categorizeRuleSet(cleanName);
        if (category === ReferralCategory.MEDICAL) {
          // Strip everything up to and including the word "medical" so any
          // prefix ("PIA Medical Claim" → "Claim") is removed and the
          // remaining text is the medical sub-category.
          const subcategory = cleanName
            .replace(/^.*\bmedical\b\s*/i, '')
            .trim();
          return {
            id: `ruleset_${cleanName}`,
            category: ReferralCategory.MEDICAL,
            title: subcategory
              ? `MEDICAL — ${subcategory.toUpperCase()}`
              : 'MEDICAL',
            body: subcategory
              ? `${subcategory} guideline triggered`
              : 'Medical guideline triggered',
          };
        }
        return {
          id: `ruleset_${cleanName}`,
          category: ReferralCategory.OTHER,
          title: cleanName.toUpperCase(),
          body: `${cleanName} guideline triggered`,
        };
      })
  );
}

function missingEvidenceReasons(
  ruleResults: IRuleResults | undefined,
): ReferralReason[] {
  if (!ruleResults?.missingEvidences?.length) return [];
  return ruleResults.missingEvidences
    .filter(evidence => evidence.decision === Decision.RUW)
    .map(evidence => {
      const name = evidence.evidenceName?.trim();
      const reason = evidence.reason?.trim();
      const titleSuffix = name ? ` — ${name.toUpperCase()}` : '';
      return {
        id: `missing_evidence_${name || evidence.evidenceName}`,
        category: ReferralCategory.OTHER,
        title: `MISSING EVIDENCE${titleSuffix}`,
        body:
          reason ||
          (name
            ? `${name} not yet available — review before decision`
            : 'Required evidence not yet available — review before decision'),
      };
    });
}

function priorPolicyRescissionReason(
  isRecommendedForRescission: boolean,
): ReferralReason | null {
  if (!isRecommendedForRescission) return null;
  return {
    id: 'prior_policy_risk_rescission',
    category: ReferralCategory.PRIOR_POLICY_RISK,
    title: 'PRIOR POLICY RISK',
    body: 'Past policies recommended for rescission',
  };
}

export interface AggregateInput {
  ruleResults?: IRuleResults;
  decisions: IDecision[];
  isRecommendedForRescission: boolean;
}

export function aggregateRuwReasons(input: AggregateInput): ReferralReason[] {
  const reasons: ReferralReason[] = [];
  const saharaDeclineToRuw = isSaharaDeclineToRuw(input.ruleResults);
  reasons.push(...decisionMessageReasons(input.decisions, saharaDeclineToRuw));
  reasons.push(...ruleSetReasons(input.ruleResults));
  reasons.push(...missingEvidenceReasons(input.ruleResults));
  const rescission = priorPolicyRescissionReason(
    input.isRecommendedForRescission,
  );
  if (rescission) reasons.push(rescission);
  return dedupeAndSort(reasons);
}

function dedupeAndSort(reasons: ReferralReason[]): ReferralReason[] {
  const seenIds = new Set<string>();
  const seenBodies = new Set<string>();
  const unique: ReferralReason[] = [];
  reasons.forEach(reason => {
    if (seenIds.has(reason.id) || seenBodies.has(reason.body)) return;
    seenIds.add(reason.id);
    seenBodies.add(reason.body);
    unique.push(reason);
  });
  return unique.sort(
    (a, b) => CATEGORY_PRIORITY[a.category] - CATEGORY_PRIORITY[b.category],
  );
}
